import { useEffect, useRef } from "react";
import { useAuthStore } from "../authStore";
import { useAppStore, useServersStore, useQueriesStore, useVaultStore, selectServer, reconcileSignedInWorkspaceAfterAuth, scheduleRuntimeWorkspaceSync } from "../store";
import { pullAllUserData, openUserSyncStream, type SyncStreamEvent } from "../userDataClient";
import { initAccountVault } from "../accountVault";
import { authFetch } from "../authClient";
import { useSyncStatusStore } from "../syncStatusStore";
import * as db from "../persistence";
import type { UserServerRow, UserQueryRow, UserVaultEntryRow } from "../userDataTypes";
import { normalizeQueryRiskLevel, normalizeRestParams } from "@spuerhund/shared/queryMetadata";
import { normalizeRequestType, normalizeServerKind } from "@spuerhund/shared/targets";
import { switchToSignedInDb, switchToAnonymousDb } from "../dbContext";
import { usePlanStore, type Plan } from "../planStore";
import { computeStaleQueryIds, computeStaleServerIds, computeStaleVaultEntryIds } from "../userDataReconcile";

/**
 * Manages user data sync when the signed-in workspace is active.
 * - Pulls all user data from D1 on activation
 * - Initializes account-derived vault
 * - Writes remote data into IndexedDB + Zustand stores
 * - Opens SSE stream for cross-device updates with exponential backoff reconnect
 */
export function useUserDataSync() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const mode = useAppStore((s) => s.mode);
  const streamRef = useRef<{ close: () => void } | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffRef = useRef(1000); // start at 1s

  useEffect(() => {
    if (!isAuthenticated || !user || mode !== "signed-in") {
      streamRef.current?.close();
      streamRef.current = null;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      useSyncStatusStore.getState().setStatus("idle");
      // Switch back to anonymous DB and refresh stores
      void switchToAnonymousDb().then(async () => {
        const servers = await db.listServers();
        useServersStore.getState().setServers(servers);
        const { activeServerId } = useServersStore.getState();
        if (activeServerId) {
          const queries = await db.listQueries(activeServerId);
          useQueriesStore.getState().setQueries(queries);
        }
      });
      return;
    }

    let cancelled = false;

    const reconnect = (delay: number) => {
      if (cancelled) return;
      reconnectTimeoutRef.current = setTimeout(() => {
        if (cancelled) return;
        streamRef.current?.close();
        // Re-pull all data to catch any changes missed during the stream gap
        void pullAllUserData().then(async (remoteData) => {
          if (cancelled) return;
          for (const remote of remoteData.servers) await mergeRemoteServer(remote);
          for (const remote of remoteData.queries) await mergeRemoteQuery(remote);

          // Reconcile: remove local entries that no longer exist in D1
          const remoteServerIds = new Set(remoteData.servers.map((s) => s.id));
          const remoteQueryIds = new Set(remoteData.queries.map((q) => q.id));
          const localServers = await db.listServers();
          const localQueries = await db.listAllQueries();
          for (const id of computeStaleServerIds(localServers.map((s) => s.id), remoteServerIds)) {
            await db.deleteServer(id);
          }
          for (const id of computeStaleQueryIds(localQueries, remoteQueryIds)) {
            await db.deleteQuery(id);
          }

          const servers = await db.listServers();
          useServersStore.getState().setServers(servers);
          scheduleRuntimeWorkspaceSync();
        }).catch(() => { /* ignore pull errors, still reconnect */ }).finally(() => {
          if (!cancelled) connectStream();
        });
      }, delay);
    };

    const connectStream = () => {
      if (cancelled) return;

      const stream = openUserSyncStream((event: SyncStreamEvent) => {
        backoffRef.current = 1000; // reset on successful event
        void handleSyncEvent(event);
      });
      streamRef.current = stream;

      stream.ready.then(() => {
        if (!cancelled) {
          useSyncStatusStore.getState().setSynced();
          // Stream ended normally (55s server timeout) — reconnect to resume live updates
          reconnect(500);
        }
      }).catch(() => {
        if (cancelled) return;
        useSyncStatusStore.getState().setStatus("offline");
        const delay = backoffRef.current;
        backoffRef.current = Math.min(delay * 2, 30_000);
        reconnect(delay);
      });
    };

    const activate = async () => {
      try {
        useSyncStatusStore.getState().setStatus("syncing");

        // Switch to user-scoped IndexedDB
        await switchToSignedInDb(user.id);

        // 1. Fetch user profile for vault init
        const profileRes = await authFetch("/account/profile");
        if (!profileRes.ok) throw new Error("Failed to fetch profile");
        const profile = (await profileRes.json()) as {
          vaultSalt: string | null;
          encryptedVaultKey: string | null;
          plan?: string;
          planPeriod?: string | null;
          planExpiresAt?: string | null;
        };
        if (cancelled) return;

        // 2. Set plan from profile
        if (profile.plan) {
          usePlanStore.getState().setPlan(
            profile.plan as Plan,
            profile.planPeriod,
            profile.planExpiresAt,
          );
        }

        // 3. Initialize account-derived vault (no passphrase needed)
        await initAccountVault(user.id, profile);
        if (cancelled) return;

        // 3. Pull all user data from D1
        const remoteData = await pullAllUserData();
        if (cancelled) return;

        // 4. Write pulled servers into IndexedDB (merge remote → local)
        for (const remote of remoteData.servers) {
          await mergeRemoteServer(remote);
        }

        // 5. Write pulled queries into IndexedDB
        for (const remote of remoteData.queries) {
          await mergeRemoteQuery(remote);
        }

        // 5b. Merge pulled vault entries into IndexedDB
        if (remoteData.vaultEntries) {
          for (const remote of remoteData.vaultEntries) {
            await mergeRemoteVaultEntry(remote);
          }
          // Refresh vault store
          const entries = await db.listVaultEntries();
          useVaultStore.getState().setSnapshot({
            ...useVaultStore.getState(),
            entries,
          });
        }

        // 5c. Reconcile: remove local entries that no longer exist in D1
        const remoteServerIds = new Set(remoteData.servers.map((s) => s.id));
        const remoteQueryIds = new Set(remoteData.queries.map((q) => q.id));
        const remoteVaultEntryIds = new Set((remoteData.vaultEntries ?? []).map((v) => v.id));

        const localServers = await db.listServers();
        const localQueries = await db.listAllQueries();
        const localVaultEntries = await db.listVaultEntries();

        for (const id of computeStaleServerIds(localServers.map((s) => s.id), remoteServerIds)) {
          await db.deleteServer(id);
        }
        for (const id of computeStaleQueryIds(localQueries, remoteQueryIds)) {
          await db.deleteQuery(id);
        }
        for (const id of computeStaleVaultEntryIds(localVaultEntries.map((v) => v.id), remoteVaultEntryIds)) {
          await db.deleteVaultEntry(id);
        }

        // 6. Refresh Zustand stores from IndexedDB
        const servers = await db.listServers();
        useServersStore.getState().setServers(servers);
        const { activeServerId } = useServersStore.getState();
        if (!activeServerId && servers.length > 0) {
          await selectServer(servers[0]!.id);
        } else if (activeServerId) {
          const queries = await db.listQueries(activeServerId);
          useQueriesStore.getState().setQueries(queries);
        }

        await reconcileSignedInWorkspaceAfterAuth();
        if (cancelled) return;

        if (cancelled) return;

        useSyncStatusStore.getState().setSynced();

        // 7. Open SSE stream for live updates
        connectStream();
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        if (message.includes("Session expired") || message.includes("Not authenticated")) {
          if (!cancelled) useSyncStatusStore.getState().setStatus("idle");
          return;
        }
        console.error("[user-data-sync] activation failed", error);
        if (!cancelled) useSyncStatusStore.getState().setStatus("error");
      }
    };

    void activate();

    return () => {
      cancelled = true;
      streamRef.current?.close();
      streamRef.current = null;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [isAuthenticated, user, mode]);
}

// ── Merge remote data into IndexedDB ─────────────────────────────────

async function mergeRemoteServer(remote: UserServerRow): Promise<void> {
  const localServers = await db.listServers();
  const existing = localServers.find((s) => s.id === remote.id);

  if (!existing || remote.updated_at > existing.updatedAt) {
    const serverFields: Record<string, unknown> = {
      name: remote.name,
      serverKind: normalizeServerKind(remote.server_kind),
      connectionConfig: remote.connection_config ? JSON.parse(remote.connection_config) : undefined,
      baseUrl: remote.base_url,
      graphqlPath: remote.graphql_path ?? undefined,
      restPath: remote.rest_path ?? undefined,
      authType: remote.auth_type,
      authConfig: remote.auth_config,
      isProduction: !!remote.is_production,
      ...(remote.timeout_ms != null ? { timeoutMs: remote.timeout_ms } : {}),
      sortOrder: remote.sort_order,
    };

    if (existing) {
      await db.updateServer(remote.id, serverFields as Partial<db.StoredServer>);
    } else {
      await db.createServer({
        id: remote.id,
        name: remote.name,
        serverKind: normalizeServerKind(remote.server_kind),
        ...(remote.connection_config ? { connectionConfig: JSON.parse(remote.connection_config) } : {}),
        baseUrl: remote.base_url,
        authType: remote.auth_type,
        authConfig: remote.auth_config,
        ...(remote.graphql_path ? { graphqlPath: remote.graphql_path } : {}),
        ...(remote.rest_path ? { restPath: remote.rest_path } : {}),
        ...(remote.is_production ? { isProduction: true } : {}),
        ...(remote.timeout_ms != null ? { timeoutMs: remote.timeout_ms } : {}),
      });
    }
  }
}

async function mergeRemoteQuery(remote: UserQueryRow): Promise<void> {
  const localQueries = await db.listQueries(remote.server_id);
  const existing = localQueries.find((q) => q.id === remote.id);

  if (!existing || remote.updated_at > existing.updatedAt) {
    const queryUpdates: Record<string, unknown> = {
      name: remote.name,
      queryText: remote.query_text,
      variables: remote.variables,
      headers: remote.headers,
      remarks: remote.remarks ?? "",
      riskLevel: normalizeQueryRiskLevel(remote.risk_level),
      sortOrder: remote.sort_order,
      pinnedAt: remote.pinned_at,
      category: remote.category ?? undefined,
      requestType: normalizeRequestType(remote.request_type),
      operationConfig: remote.operation_config ? JSON.parse(remote.operation_config) : undefined,
      restMethod: remote.rest_method ?? undefined,
      restPath: remote.rest_path ?? undefined,
      restParams: remote.rest_params ? normalizeRestParams(JSON.parse(remote.rest_params)) : [],
      restContentType: remote.rest_content_type ?? undefined,
    };

    if (existing) {
      await db.updateQuery(remote.id, queryUpdates as Partial<db.StoredQuery>);
    } else {
      await db.createQuery({
        id: remote.id,
        serverId: remote.server_id,
        name: remote.name,
        queryText: remote.query_text,
        variables: remote.variables,
        headers: remote.headers,
        remarks: remote.remarks ?? "",
        riskLevel: normalizeQueryRiskLevel(remote.risk_level),
        pinnedAt: remote.pinned_at,
        ...(remote.category ? { category: remote.category } : {}),
        ...(remote.request_type ? { requestType: normalizeRequestType(remote.request_type) } : {}),
        ...(remote.operation_config ? { operationConfig: JSON.parse(remote.operation_config) } : {}),
        ...(remote.rest_method ? { restMethod: remote.rest_method as db.RestMethod } : {}),
        ...(remote.rest_path ? { restPath: remote.rest_path } : {}),
        ...(remote.rest_params ? { restParams: normalizeRestParams(JSON.parse(remote.rest_params)) } : {}),
        ...(remote.rest_content_type ? { restContentType: remote.rest_content_type as db.RestContentType } : {}),
      });
    }
  }
}

// ── Vault entry merge ─────────────────────────────────────────────────

function parseEncryptedValue(packed: string): { iv: string; ciphertext: string } {
  const colonIdx = packed.indexOf(":");
  if (colonIdx < 0) throw new Error("Invalid encrypted_value format");
  return { iv: packed.slice(0, colonIdx), ciphertext: packed.slice(colonIdx + 1) };
}

async function mergeRemoteVaultEntry(remote: UserVaultEntryRow): Promise<void> {
  const existing = await db.getVaultEntry(remote.id);
  if (existing && remote.updated_at <= existing.updatedAt) return;

  const { iv, ciphertext } = parseEncryptedValue(remote.encrypted_value);

  if (existing) {
    await db.updateVaultEntry(remote.id, {
      key: remote.key,
      kind: remote.kind as db.VaultEntryKind,
      scopeType: remote.server_id ? "server" : "global",
      ...(remote.server_id ? { scopeServerId: remote.server_id } : { scopeServerId: undefined }),
      ciphertext,
      iv,
    });
  } else {
    // createVaultEntry generates a new id, but we need to preserve the remote id.
    // Use the lower-level persistence store directly.
    const idb = await db.getDb();
    const entry: db.StoredVaultEntry = {
      id: remote.id,
      key: remote.key,
      kind: remote.kind as db.VaultEntryKind,
      scopeType: remote.server_id ? "server" : "global",
      ...(remote.server_id ? { scopeServerId: remote.server_id } : {}),
      description: "",
      ciphertext,
      iv,
      createdAt: remote.created_at,
      updatedAt: remote.updated_at,
    };
    const tx = idb.transaction("vaultEntries", "readwrite");
    tx.objectStore("vaultEntries").put(entry);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}

// ── SSE event handler ────────────────────────────────────────────────

async function handleSyncEvent(event: SyncStreamEvent): Promise<void> {
  try {
    useSyncStatusStore.getState().setSynced();
    switch (event.type) {
      case "server-changed": {
        const remote = event.data as UserServerRow;
        await mergeRemoteServer(remote);
        const servers = await db.listServers();
        useServersStore.getState().setServers(servers);
        scheduleRuntimeWorkspaceSync();
        break;
      }
      case "query-changed": {
        const remote = event.data as UserQueryRow;
        await mergeRemoteQuery(remote);
        const { activeServerId } = useServersStore.getState();
        if (activeServerId === remote.server_id) {
          const queries = await db.listQueries(activeServerId);
          useQueriesStore.getState().setQueries(queries);
        }
        scheduleRuntimeWorkspaceSync();
        break;
      }
      case "vault-changed": {
        const remote = event.data as UserVaultEntryRow;
        await mergeRemoteVaultEntry(remote);
        const entries = await db.listVaultEntries();
        useVaultStore.getState().setSnapshot({
          ...useVaultStore.getState(),
          entries,
        });
        break;
      }
      case "vault-deleted": {
        const { id } = event.data as { id: string };
        await db.deleteVaultEntry(id);
        const entries = await db.listVaultEntries();
        useVaultStore.getState().setSnapshot({
          ...useVaultStore.getState(),
          entries,
        });
        break;
      }
      case "ready":
        break;
      default:
        break;
    }
  } catch (error) {
    console.warn("[user-data-sync] event handling failed", error);
  }
}
